/**
 * `/config` redirect — phase 41 follow-up.
 *
 * Phase 41 shipped /config as a multi-studio overview page (showing
 * file paths + raw YAML + parsed shape per studio) AND /config/:slug as
 * the per-studio editor. Mario asked to collapse the overview: he
 * doesn't want to see file paths or raw YAML, just the editor.
 *
 * This component resolves the active studio's slug from /api/whoami
 * and redirects to /config/<slug>. At MVP there's exactly one studio
 * (E-Pulsive Belgravia) so the redirect always lands at the same
 * place. When customer #2 onboards, each staff member's JWT carries
 * their own studio's id; whoami resolves it; this component picks the
 * right one for that user.
 *
 * No props. Loading state is brief (one cached /api/whoami round-trip).
 */

import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { api, ApiError } from "../lib/api";

export function ConfigRedirect(): JSX.Element {
  const [slug, setSlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .whoami()
      .then((data) => {
        if (cancelled) return;
        setSlug(data.studio.slug);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        if (e instanceof ApiError) {
          setError(`api_error:${e.status}${e.code ? `:${e.code}` : ""}`);
        } else {
          setError(e.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error !== null) {
    return (
      <div className="page-studio-config">
        <h1>Studio config</h1>
        <div className="tag tag-danger">Failed to resolve studio: {error}</div>
      </div>
    );
  }
  if (slug === null) {
    return (
      <div className="page-studio-config">
        <h1>Studio config</h1>
        <p className="subtle">Loading…</p>
      </div>
    );
  }
  return <Navigate to={`/config/${encodeURIComponent(slug)}`} replace />;
}
